import React, { useState, useRef, useEffect, useMemo } from "react";
import "./chatBot.css";
import useStore from "../../../useStore";

const hasHTML = (text = "") => /<[^>]+>/.test(text);
const stripHTML = (html = "") => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const urlRegex = /(https?:\/\/[^\s<]+)/gi;
const escapeHtml = (str = "") =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const linkify = (text = "") =>
  escapeHtml(text).replace(
    urlRegex,
    (u) => `<a href="${u}" target="_blank" rel="noopener noreferrer">${u}</a>`
  );

const smartLinkify = (raw = "") => {
  const lines = raw.split(/\n/);
  return lines
    .map((line) => {
      const m = line.match(
        /^\s*(?:•|-)?\s*(.+?)\s*(?:→|:)\s*(https?:\/\/[^\s<]+)\s*$/i
      );
      if (m) {
        const safeName = escapeHtml(m[1].trim());
        const url = m[2].trim();
        const hadBullet = /^\s*•/.test(line);
        return `${hadBullet ? "• " : ""}<a href="${url}" target="_blank" rel="noopener noreferrer">${safeName}</a>`;
      }
      return linkify(line);
    })
    .join("<br/>");
};

const hasURL = (text = "") => urlRegex.test(text);

const calcCharDelay = (text, maxMs = 5000, minDelay = 8, maxDelay = 35) => {
  const len = Math.max(1, text.length);
  const delay = Math.floor(maxMs / len);
  return Math.min(maxDelay, Math.max(minDelay, delay));
};

const DEFAULT_ICON = "➤";
const getOptionIcon = (icon, value = "") => {
  if (icon) return icon;
  const icons = { camion: "🚛", paquete: "📦", sello: "🔒", seguridad: "🛡️", precio: "💰", contacto: "📞" };
  return icons[value.toLowerCase()] || DEFAULT_ICON;
};

export default function ChatBot({ onClose }) {
  const [input, setInput] = useState("");
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [usedOptionIndexes, setUsedOptionIndexes] = useState(new Set());
  const messagesEndRef = useRef(null);

  const { chatBot, chatLoading, error, sendChatMessage } = useStore();

  const initialMsgs = useMemo(
    () => [
      {
        role: "bot",
        text: "¡Hola! Soy el asistente virtual de **Quality Seal**.\n¿En qué puedo ayudarte hoy?",
      },
    ],
    []
  );

  const history = useMemo(() => [...initialMsgs, ...chatBot], [initialMsgs, chatBot]);

  const [renderedHistory, setRenderedHistory] = useState(history);
  const animatedLengthRef = useRef(history.length);

  useEffect(() => {
    if (history.length <= animatedLengthRef.current) return;

    const last = history[history.length - 1];
    animatedLengthRef.current = history.length;

    if (last?.role === "bot" && typeof last.text === "string") {
      const rawAnswer = last.text;
      const wasHTML = hasHTML(rawAnswer);
      const displayText = wasHTML ? stripHTML(rawAnswer) : rawAnswer;

      setRenderedHistory([...history.slice(0, -1), { ...last, text: "" }]);
      setShowTypingIndicator(true);

      const speed = calcCharDelay(displayText, 5000);
      let i = 0;
      let active = true;
      const interval = setInterval(() => {
        if (!active) return;
        i++;
        const chunk = displayText.slice(0, i);
        setRenderedHistory((prevList) => {
          const copy = [...prevList];
          copy[copy.length - 1] = { ...copy[copy.length - 1], text: chunk, _renderHtml: false };
          return copy;
        });

        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

        if (i >= displayText.length) {
          clearInterval(interval);
          setRenderedHistory((prevList) => {
            const copy = [...prevList];
            const idx = copy.length - 1;
            const containsUrl = hasURL(displayText);
            copy[idx] = {
              ...copy[idx],
              text: wasHTML ? rawAnswer : (containsUrl ? smartLinkify(displayText) : displayText),
              _renderHtml: wasHTML || containsUrl,
            };
            return copy;
          });
          setShowTypingIndicator(false);
        }
      }, speed);

      return () => {
        active = false;
        clearInterval(interval);
      };
    } else {
      setRenderedHistory(history);
    }
  }, [history]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [renderedHistory, showTypingIndicator, chatLoading]);

  const onSend = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text) return;
    setInput("");
    await sendChatMessage(text);
  };

  const handleOptionClick = (msgIndex, value) => {
    setUsedOptionIndexes((prev) => new Set([...prev, msgIndex]));
    onSend(value);
  };

  const renderText = (m) => {
    const text = m.text || "";
    if (m._renderHtml) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    if (hasURL(text)) {
      return <span dangerouslySetInnerHTML={{ __html: smartLinkify(text) }} />;
    }
    // Negrita básica **texto**
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return (
      <span>
        {parts.map((p, idx) => (idx % 2 === 1 ? <strong key={idx}>{p}</strong> : p))}
      </span>
    );
  };

  return (
    <div className="chatbot-window">
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-header-info">
          <div className="chatbot-avatar">
            <span>🤖</span>
          </div>
          <div>
            <div className="chatbot-header-name">Asistente QS</div>
            <div className="chatbot-header-status">
              <span className="chatbot-status-dot" />
              En línea
            </div>
          </div>
        </div>
        <button className="chatbot-close-btn" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>
      </div>

      {/* Mensajes */}
      <div className="chatbot-messages">
        {renderedHistory.map((m, i) => {
          const isBot = m.role === "bot";
          const isAnimating = showTypingIndicator && i === renderedHistory.length - 1 && isBot;

          // Las opciones vienen directamente del backend (m.options)
          const options = isBot ? (m.options || []) : [];
          const optionsAlreadyUsed = usedOptionIndexes.has(i);

          return (
            <div key={i} className={`chatbot-row ${isBot ? "bot-row" : "user-row"}`}>
              {isBot && <div className="chatbot-msg-avatar">🤖</div>}
              <div className={`chatbot-bubble ${isBot ? "bot-bubble" : "user-bubble"}`}>
                {renderText(m)}

                {/* Opciones seleccionables — solo después de que termine la animación */}
                {options.length > 0 && !isAnimating && !optionsAlreadyUsed && (
                  <div className="chatbot-options">
                    {options.map((opt, oi) => (
                      <button
                        key={oi}
                        className="chatbot-option-chip"
                        onClick={() => handleOptionClick(i, opt.value)}
                        disabled={chatLoading || isAnimating}
                      >
                        <span className="chip-icon">{getOptionIcon(opt.icon, opt.value)}</span>
                        <span className="chip-label">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Chips desactivados tras selección */}
                {options.length > 0 && !isAnimating && optionsAlreadyUsed && (
                  <div className="chatbot-options">
                    {options.map((opt, oi) => (
                      <span key={oi} className="chatbot-option-chip used">
                        <span className="chip-icon">{getOptionIcon(opt.icon, opt.value)}</span>
                        <span className="chip-label">{opt.label}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {(showTypingIndicator || chatLoading) && (
          <div className="chatbot-row bot-row">
            <div className="chatbot-msg-avatar">🤖</div>
            <div className="bot-bubble typing-bubble">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        )}

        {error && (
          <div className="chatbot-row bot-row">
            <div className="chatbot-msg-avatar">🤖</div>
            <div className="bot-bubble error-bubble">
              ⚠️ Ocurrió un error. Intenta de nuevo más tarde.
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chatbot-input-row">
        <input
          className="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          placeholder="Escribe tu mensaje…"
          disabled={chatLoading}
        />
        <button
          className="chatbot-send-btn"
          onClick={() => onSend()}
          disabled={chatLoading || !input.trim()}
          aria-label="Enviar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
